// traceSkillRuns.js — which SKILLS the scan window's agents ran, and what work
// each run actually drove, reconstructed from raw request bodies (the same
// hydration mechanic as the Files/Linear/Research/Forge X-rays).
//
// Why skills matter to an X-ray suite: measured on live traffic, sessions
// containing a skill invocation carry a hugely disproportionate share of the
// window's activity, and most of that activity flows through the OPAQUE
// channel (shell commands) rather than the transparent tools the other tabs
// parse. The skill name is the one legible intent label riding above that
// stream — "forgejo", "backend-hotfix-release", "docx" — so this tab attaches
// the label to the work: each run, then the channel mix of everything the
// session did under it.
//
// Skills enter a session through TWO doors, and only one is a tool call:
//
//   tool    — a `Skill` tool_use block ({ skill, args }). Visible to any
//             tool-name matcher.
//   command — the user typed `/name args`. NO tool call exists; the harness
//             stamps `<command-name>/name</command-name>` (+ command-args)
//             markers into the user turn's text. A detector keyed on tool
//             names alone never sees these.
//
// Attribution is positional, not causal: a session's tool-use marks are
// assigned to the most recent skill run at-or-before their prompt, "before
// any skill" work stays unattributed. Prompt-granular (every mark in a prompt
// shares its timestamp), which is exactly the granularity the window gives us.

import { classifyForgeCommand, splitStatements, stripHeredocs, SHELL_TOOL_RE } from "./traceForgeOps.js";

// Harness control commands are not skills — they steer the session, do no work.
// Kept as a separate "controls" list (still worth showing: /model flips are
// context), never aggregated as skill runs. A hardcoded snapshot with no
// shared source of truth (the harness doesn't publish its control set) — an
// unlisted new control degrades gracefully: it shows as a "skill" whose runs
// carry near-zero footprint, until it's added here.
const BUILTIN_COMMANDS = new Set([
  "model", "clear", "compact", "resume", "login", "logout", "mcp", "plugin",
  "plugins", "reload-plugins", "effort", "plan", "status", "help", "config",
  "cost", "context", "memory", "hooks", "agents", "doctor", "permissions",
  "exit", "quit", "skills", "fast", "rewind", "export", "usage", "upgrade",
  "vim", "terminal-setup", "theme", "bug", "release-notes", "todos", "ide",
  "add-dir", "bashes", "install-github-app",
]);

// Args capture cap — skill args are one-line task briefs, not documents.
const ARGS_CAP = 600;
// Distinct shell verbs kept per command / per run.
const VERBS_PER_COMMAND_CAP = 8;
const TOP_VERBS_CAP = 10;

const FILE_TOOL_RE = /^(write|edit|multiedit|notebookedit|apply_patch|create_file|str_replace_editor|write_file|save_file)$/i;
const READ_TOOL_RE = /^(read|read_file|readfile|grep|glob|view|open_file|notebookread|ls|list_dir|search)$/i;
const WEB_TOOL_RE = /^(webfetch|websearch|web_search|web_search_call|web_fetch|fetch_url)$/i;
const AGENT_TOOL_RE = /^(agent|task|workflow|dispatch_agent)$/i;

function str(v) {
  return typeof v === "string" && v ? v : null;
}

// djb2 — stable synthetic dedup key (same as the sibling libs).
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function capArgs(s) {
  const t = typeof s === "string" ? s.trim() : "";
  if (!t) return null;
  return t.length > ARGS_CAP ? `${t.slice(0, ARGS_CAP)}…` : t;
}

// "/plugin:entry" → { name: "plugin:entry", plugin, entry, builtin }. The
// builtin check runs on the bare name — plugins can't shadow harness controls.
export function normalizeSkillName(raw) {
  const name = String(raw || "").trim().replace(/^\//, "");
  if (!name || name.length > 120) return null;
  const i = name.indexOf(":");
  const plugin = i > 0 ? name.slice(0, i) : null;
  const entry = i > 0 ? name.slice(i + 1) : name;
  return { name, plugin, entry, builtin: !plugin && BUILTIN_COMMANDS.has(name.toLowerCase()) };
}

// ── Door 2: command markers in user-turn text ────────────────────────────────

// The harness writes `<command-name>/x</command-name>` (note: bodies observed
// on the wire escape the "/" — the parsed turn text has it plain). Args ride
// in a sibling <command-args> within the same stamp.
const COMMAND_NAME_RE = /<command-name>\s*\/?([^<\n]{1,120}?)\s*<\/command-name>/g;
const COMMAND_ARGS_RE = /<command-args>([\s\S]{0,4000}?)<\/command-args>/;

export function commandInvocationsOf(text) {
  const out = [];
  const s = String(text || "");
  let m;
  COMMAND_NAME_RE.lastIndex = 0;
  while ((m = COMMAND_NAME_RE.exec(s))) {
    const norm = normalizeSkillName(m[1]);
    if (!norm) continue;
    // Args belong to THIS stamp: look just past the name marker, stop at the
    // next command stamp so one invocation can't steal the next one's args.
    const tail = s.slice(COMMAND_NAME_RE.lastIndex);
    const nextStamp = tail.search(/<command-name>/);
    const scope = nextStamp === -1 ? tail : tail.slice(0, nextStamp);
    const am = COMMAND_ARGS_RE.exec(scope);
    out.push({ ...norm, args: capArgs(am ? am[1] : null), source: "command" });
  }
  return out;
}

// ── Activity marks — the channel fingerprint ─────────────────────────────────

// First meaningful verb of each statement: skip env assignments, cd/export/
// echo-style shell noise, and unwrap `python3 some/script.py` to the script's
// basename (that's the intent — "forgejo-pr.py", not "python3").
const NOISE_VERBS = new Set([
  "cd", "export", "echo", "set", "true", "false", "exit", "source", "alias",
  "unset", "printf", "if", "then", "else", "fi", "for", "do", "done", "while",
  "local", "return", "read", "wait", "shift", "eval", "[", "[[", "test", "{", "}",
  "import", "from", "def", "print", // python leaking through a mangled heredoc
]);
const RUNNER_VERBS = new Set(["python", "python3", "python3.14", "node", "bun", "deno", "ruby", "perl", "sh", "bash", "zsh"]);
// A verb must look like a command word. Kills residue from unparseable text:
// ALL_CAPS var fragments, `print('…` calls, flag tokens.
const VERB_SHAPE_RE = /^[a-z0-9][\w.+-]*$/i;
const ALL_CAPS_RE = /^[A-Z_][A-Z0-9_]*$/;

export function shellVerbsOf(cmd) {
  // Heredoc bodies are content, not commands — a script body must not mint
  // verbs. stripHeredocs also swallows an UNTERMINATED heredoc to the end,
  // which is exactly what a truncated captured command looks like.
  const { text } = stripHeredocs(cmd);
  const verbs = [];
  const seen = new Set();
  // Quote-aware split — a `;`/`|` inside a --body/-d payload must not mint a
  // phantom statement (and with it a bogus verb chip).
  for (const rawStmt of splitStatements(text)) {
    if (verbs.length >= VERBS_PER_COMMAND_CAP) break;
    const tokens = rawStmt.trim().split(/\s+/).filter(Boolean);
    let verb = null;
    let inSubst = false;
    for (let i = 0; i < tokens.length; i++) {
      const raw = tokens[i];
      // `TOK=$(cat some/path)` spans tokens — skip through the substitution
      // so the path never becomes a verb.
      if (inSubst) { if (raw.includes(")")) inSubst = false; continue; }
      if (/^[A-Za-z_]\w*=/.test(raw)) {
        if (raw.includes("$(") && !raw.includes(")")) inSubst = true;
        continue;
      }
      const t = raw.replace(/^["'(]+|["')]+$/g, "");
      if (!t || t === "sudo" || t === "command" || t === "exec" || t === "nohup" || t === "time") continue;
      const base = t.split("/").pop();
      if (NOISE_VERBS.has(base)) break;
      if (!VERB_SHAPE_RE.test(base) || ALL_CAPS_RE.test(base)) break;
      if (RUNNER_VERBS.has(base)) {
        const next = tokens[i + 1]?.replace(/^["']+|["']+$/g, "");
        const script = next && /\.(py|js|mjs|ts|rb|sh)$/.test(next) ? next.split("/").pop() : null;
        verb = script || base;
      } else {
        verb = base;
      }
      break;
    }
    if (verb && !seen.has(verb)) { seen.add(verb); verbs.push(verb); }
  }
  return verbs;
}

// Tool name → channel. MCP tools keep their server ("mcp:linear") — that IS
// the interesting label; everything else buckets coarsely.
export function channelOf(name) {
  const s = String(name || "");
  const last = s.split("__").pop();
  // Shell wins over the MCP-server label: mcp__workspace__bash IS shell — the
  // channel question is "what kind of work", not "which transport".
  if (SHELL_TOOL_RE.test(last)) return "shell";
  if (s.startsWith("mcp__")) {
    const segs = s.split("__");
    return `mcp:${segs.slice(1, -1).join("__") || "unknown"}`;
  }
  if (FILE_TOOL_RE.test(last)) return "files";
  if (READ_TOOL_RE.test(last)) return "reads";
  if (WEB_TOOL_RE.test(last)) return "web";
  if (AGENT_TOOL_RE.test(last)) return "agents";
  // Artifact publishes get their own bucket — for the skills that exist to
  // produce them (artifact-design, dataviz) the publish IS the deliverable.
  if (/^artifact$/i.test(last)) return "artifacts";
  return "other";
}

// An Artifact tool_use's publish metadata; the published URL only exists in
// the RESULT.
function publishOf(input, resultText) {
  const path = filePathOf(input);
  const title = str(input?.title) || baseNameOf(path);
  if (!path && !title) return null;
  const um = /https?:\/\/[^\s"'<>)\]]+/.exec(resultText || "");
  return {
    title: title || null,
    description: str(input?.description) || null,
    favicon: str(input?.favicon) || null,
    path: path || null,
    url: um ? um[0] : null,
  };
}

// ── Objects — the NOUNS a mark touched ───────────────────────────────────────
// Channel counts alone make every run look the same ("shell 44 · files 8").
// What separates runs is what they touched: the files written, the PR/issue
// numbers driven through the shell (the Forge grammar reused), the pages
// fetched, the Linear issues read, the artifacts published. Each mark keeps a
// few compact human labels; runs and skills aggregate them into a
// most-touched list.

const OBJS_PER_MARK_CAP = 4;
const TOP_OBJECTS_CAP = 12;
const LINEAR_ID_RE = /^[A-Za-z][A-Za-z0-9]{0,9}-\d{1,6}$/;

function baseNameOf(p) {
  const s = String(p || "").split(/[\\/]/).pop();
  return s || null;
}

function filePathOf(input) {
  for (const k of ["file_path", "path", "notebook_path", "filePath"]) {
    const v = input?.[k];
    if (typeof v === "string" && v) return v;
  }
  return null;
}

function hostOf(url) {
  try {
    return new URL(url).host || null;
  } catch {
    const m = /^([a-z0-9.-]+\.[a-z]{2,})/i.exec(String(url || ""));
    return m ? m[1] : null;
  }
}

export function markObjectsOf(name, input, ch) {
  const objs = [];
  const push = (label) => {
    if (label && !objs.includes(label) && objs.length < OBJS_PER_MARK_CAP) objs.push(label);
  };
  const last = String(name || "").split("__").pop().toLowerCase();
  if (last === "artifact") {
    const label = str(input?.title) || baseNameOf(filePathOf(input));
    if (label) push(`artifact “${label.slice(0, 60)}”`);
    return objs;
  }
  if (ch === "files") {
    push(`wrote ${baseNameOf(filePathOf(input)) || "?"}`);
  } else if (ch === "reads") {
    const p = baseNameOf(filePathOf(input));
    if (p) push(`read ${p}`);
    else if (str(input?.pattern)) push(`grep ${input.pattern.slice(0, 40)}`);
  } else if (ch === "web") {
    const host = hostOf(str(input?.url) || "");
    if (host) push(`web ${host}`);
    else if (str(input?.query)) push(`search “${input.query.slice(0, 40)}”`);
  } else if (ch === "shell") {
    const cmd = str(input?.command) || str(input?.cmd) || str(input?.script);
    if (cmd) {
      for (const op of classifyForgeCommand(cmd)) {
        if (op.num) push(`#${op.num}`);
        else if (op.kind === "push") push(`push ${op.fields?.ref || op.fields?.remote || ""}`.trim());
      }
    }
  } else if (ch.startsWith("mcp:") && /linear/i.test(ch)) {
    for (const k of ["issueId", "id"]) {
      const v = str(input?.[k]);
      if (v && LINEAR_ID_RE.test(v)) push(v.toUpperCase());
    }
  } else if (ch === "agents") {
    const brief = str(input?.description) || str(input?.prompt);
    if (brief) push(`agent “${brief.slice(0, 48).trim()}${brief.length > 48 ? "…" : ""}”`);
  }
  return objs;
}

// One prompt's turns → { invocations, marks }.
//   invocation: { key, name, plugin, entry, builtin, args, source }
//   mark:       { key, ch, verbs, objs } — one per tool_use block; verbs only
//               for shell, objs the touched-noun labels above.
// Both keys are stable across a session's cumulative re-sends (tool_use id,
// or a text-derived hash for command markers) so collectWindowSkillActivity
// can first-sighting-dedup exactly like the sibling libs.
export function extractSkillSignals(turns) {
  const invocations = [];
  const marks = [];
  if (!Array.isArray(turns)) return { invocations, marks };
  // Result pairing exists ONLY for publish marks (the artifact URL rides the
  // result) — everything else needs just the call side.
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  const resultTextOf = (id) => {
    const content = id ? resultById.get(id)?.content : null;
    if (typeof content === "string") return content;
    if (Array.isArray(content)) return content.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n");
    return "";
  };
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_use") {
        const name = String(b.name || "");
        const last = name.split("__").pop().toLowerCase();
        if (last === "skill" || last === "slashcommand") {
          const input = b.input && typeof b.input === "object" ? b.input : {};
          const norm = normalizeSkillName(str(input.skill) || str(input.command) || str(input.name));
          if (norm) {
            invocations.push({
              ...norm,
              args: capArgs(str(input.args) || str(input.arguments)),
              source: "tool",
              key: b.id || `tool:${hashString(norm.name + "|" + (str(input.args) || ""))}`,
            });
            continue; // the Skill call itself is not a work mark
          }
        }
        const ch = channelOf(name);
        const input = b.input && typeof b.input === "object" ? b.input : {};
        marks.push({
          key: b.id || `mark:${hashString(name + JSON.stringify(b.input || null))}`,
          ch,
          verbs: (() => {
            if (!SHELL_TOOL_RE.test(name.split("__").pop())) return null;
            const cmd = str(input.command) || str(input.cmd) || str(input.script);
            return cmd ? shellVerbsOf(cmd) : null;
          })(),
          objs: markObjectsOf(name, input, ch),
          publish: ch === "artifacts" ? publishOf(input, resultTextOf(b.id)) : null,
        });
      } else if (b?.type === "text" && turn?.role === "user") {
        // Command-door invocations exist only as text markers in user turns.
        // Assistant text QUOTING a marker (rare, but this is a dogfooding
        // tenant) must not count — hence the role gate.
        for (const inv of commandInvocationsOf(b.text)) {
          invocations.push({
            ...inv,
            key: `cmd:${hashString(inv.name + "|" + (inv.args || "").slice(0, 200))}`,
          });
        }
      }
    }
  }
  return { invocations, marks };
}

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

// Flatten per-prompt extractions into the window's real deltas — first
// sighting per session wins, the same mechanic as the sibling libs, applied
// to runs and marks independently.
export function collectWindowSkillActivity(promptEvents) {
  const sorted = [...(promptEvents || [])].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const seenRuns = new Set();
  const seenMarks = new Set();
  const runs = [];
  const marks = [];
  for (const p of sorted) {
    const meta = {
      promptId: p.promptId,
      sessionId: p.sessionId || "",
      endpointId: p.endpointId || "",
      endpointName: p.endpointName || "",
      ts: p.ts ?? null,
    };
    for (const inv of p.invocations || []) {
      const dedupKey = `${meta.sessionId}|${inv.key}`;
      if (seenRuns.has(dedupKey)) continue;
      seenRuns.add(dedupKey);
      // The invoking prompt's one-line summary is the run's BRIEF when the
      // skill took no args — "what was this run about" beats a bare timestamp.
      runs.push({ ...inv, ...meta, summary: str(p.summary) });
    }
    for (const mark of p.marks || []) {
      const dedupKey = `${meta.sessionId}|${mark.key}`;
      if (seenMarks.has(dedupKey)) continue;
      seenMarks.add(dedupKey);
      marks.push({ ...mark, ...meta });
    }
  }
  return { runs, marks };
}

function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : "unknown";
}

function emptyFootprint() {
  return { shell: 0, files: 0, reads: 0, web: 0, agents: 0, artifacts: 0, other: 0, mcp: new Map(), verbs: new Map(), objects: new Map(), prompts: new Set() };
}

function foldMark(fp, mark) {
  if (mark.ch.startsWith("mcp:")) {
    const server = mark.ch.slice(4);
    fp.mcp.set(server, (fp.mcp.get(server) || 0) + 1);
  } else {
    fp[mark.ch] = (fp[mark.ch] || 0) + 1;
  }
  for (const v of mark.verbs || []) fp.verbs.set(v, (fp.verbs.get(v) || 0) + 1);
  for (const o of mark.objs || []) fp.objects.set(o, (fp.objects.get(o) || 0) + 1);
  if (mark.promptId) fp.prompts.add(mark.promptId);
}

function finishFootprint(fp) {
  return {
    shell: fp.shell, files: fp.files, reads: fp.reads, web: fp.web,
    agents: fp.agents, artifacts: fp.artifacts, other: fp.other,
    mcp: [...fp.mcp.entries()].map(([server, n]) => ({ server, n })).sort((a, b) => b.n - a.n),
    verbs: [...fp.verbs.entries()].map(([verb, n]) => ({ verb, n })).sort((a, b) => b.n - a.n).slice(0, TOP_VERBS_CAP),
    // Writes-and-targets lead reads: "wrote X / #611" says more about a run
    // than its most-re-read file, so ties break against `read …` labels.
    objects: [...fp.objects.entries()].map(([label, n]) => ({ label, n }))
      .sort((a, b) => (b.n - a.n) || (a.label.startsWith("read ") ? 1 : 0) - (b.label.startsWith("read ") ? 1 : 0))
      .slice(0, TOP_OBJECTS_CAP),
    prompts: fp.prompts.size,
    total: fp.shell + fp.files + fp.reads + fp.web + fp.agents + fp.artifacts + fp.other
      + [...fp.mcp.values()].reduce((s, n) => s + n, 0),
  };
}

// A run's publishes, deduped by backing path: republishing the same page is
// ONE artifact with n deploys; the newest metadata wins (titles get edited).
function collectPublishes(raw) {
  const byPath = new Map();
  for (const p of raw || []) {
    const key = p.path || p.title || "?";
    const prev = byPath.get(key);
    if (prev) {
      prev.n += 1;
      prev.ts = p.ts ?? prev.ts;
      prev.promptId = p.promptId || prev.promptId;
      if (p.title) prev.title = p.title;
      if (p.description) prev.description = p.description;
      if (p.favicon) prev.favicon = p.favicon;
      if (p.url) prev.url = p.url;
    } else {
      byPath.set(key, { ...p, n: 1, firstTs: p.ts ?? null });
    }
  }
  return [...byPath.values()].sort((a, b) => (tsOf(a.firstTs) ?? 0) - (tsOf(b.firstTs) ?? 0));
}

// The window's skill activity — skill-, run-, and actor-centric:
//   skills:   [{ name, plugin, entry, runs:[run…], sessions, sources:{tool,
//               command}, footprint, }] — real skills only, most-worked first
//   controls: [{ name, uses, sessions }] — /model-style harness controls
//   actors:   [{ id, name, runs, skills, sessions }]
//   totals:   { runs, skills, controlUses, sessions, attributedMarks,
//               unattributedMarks, sources: { tool, command } }
// Each run carries its own footprint: every tool-use mark in its session from
// the run's prompt up to the next run (positional attribution — see header).
export function buildSkillActivity({ runs, marks }) {
  const bySession = new Map();
  for (const r of runs || []) {
    if (r.builtin) continue;
    let s = bySession.get(r.sessionId);
    if (!s) { s = { runs: [], marks: [] }; bySession.set(r.sessionId, s); }
    s.runs.push({ ...r, fp: emptyFootprint() });
  }
  let attributed = 0;
  let unattributed = 0;
  for (const m of marks || []) {
    const s = bySession.get(m.sessionId);
    if (!s) { unattributed += 1; continue; }
    s.marks.push(m);
  }
  for (const s of bySession.values()) {
    // Stable ts sort; runs in the same prompt keep insertion order and a mark
    // ties to the LATEST run at-or-before it.
    s.runs.sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
    for (const m of s.marks) {
      const mts = tsOf(m.ts) ?? 0;
      let owner = null;
      for (const r of s.runs) {
        const rts = tsOf(r.ts) ?? 0;
        if (rts <= mts) owner = r;
        else break;
      }
      if (owner) {
        foldMark(owner.fp, m);
        attributed += 1;
        if (m.publish) {
          (owner.rawPublishes ||= []).push({ ...m.publish, ts: m.ts, promptId: m.promptId, sessionId: m.sessionId });
        }
      } else unattributed += 1;
    }
  }

  const skills = new Map();
  const controls = new Map();
  const actors = new Map();
  let toolRuns = 0;
  let commandRuns = 0;
  for (const r of runs || []) {
    if (r.builtin) {
      let c = controls.get(r.name);
      if (!c) { c = { name: r.name, uses: 0, sessions: new Set() }; controls.set(r.name, c); }
      c.uses += 1;
      c.sessions.add(r.sessionId);
      continue;
    }
    if (r.source === "tool") toolRuns += 1;
    else commandRuns += 1;
    let actor = actors.get(r.endpointId);
    if (!actor) {
      actor = { id: r.endpointId, name: r.endpointName || shortId(r.endpointId), runs: 0, skills: new Set(), sessions: new Set() };
      actors.set(r.endpointId, actor);
    }
    actor.runs += 1;
    actor.skills.add(r.name);
    actor.sessions.add(r.sessionId);
  }

  for (const s of bySession.values()) {
    for (const r of s.runs) {
      let sk = skills.get(r.name);
      if (!sk) {
        sk = {
          name: r.name, plugin: r.plugin, entry: r.entry,
          runs: [], sessions: new Set(), sources: { tool: 0, command: 0 },
          fp: emptyFootprint(),
        };
        skills.set(r.name, sk);
      }
      const footprint = finishFootprint(r.fp);
      sk.runs.push({
        key: r.key, args: r.args, summary: r.summary || null, source: r.source,
        promptId: r.promptId, sessionId: r.sessionId,
        endpointId: r.endpointId, endpointName: r.endpointName,
        ts: r.ts, footprint,
        publishes: collectPublishes(r.rawPublishes),
      });
      sk.sessions.add(r.sessionId);
      sk.sources[r.source] += 1;
      // Aggregate: fold the run's finished footprint into the skill's raw one.
      sk.fp.shell += footprint.shell; sk.fp.files += footprint.files;
      sk.fp.reads += footprint.reads; sk.fp.web += footprint.web;
      sk.fp.agents += footprint.agents; sk.fp.other += footprint.other;
      for (const { server, n } of footprint.mcp) sk.fp.mcp.set(server, (sk.fp.mcp.get(server) || 0) + n);
      for (const { verb, n } of footprint.verbs) sk.fp.verbs.set(verb, (sk.fp.verbs.get(verb) || 0) + n);
      for (const { label, n } of footprint.objects) sk.fp.objects.set(label, (sk.fp.objects.get(label) || 0) + n);
    }
  }

  const skillList = [...skills.values()].map((sk) => {
    const footprint = finishFootprint(sk.fp);
    // A skill's prompt count is the sum over its runs — the fold above only
    // carries channel counts, not the per-run prompt sets.
    footprint.prompts = sk.runs.reduce((s, r) => s + r.footprint.prompts, 0);
    return {
      name: sk.name, plugin: sk.plugin, entry: sk.entry,
      runs: [...sk.runs].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0)),
      sessions: sk.sessions.size,
      sources: sk.sources,
      footprint,
    };
  });
  skillList.sort((a, b) => (b.footprint.total - a.footprint.total) || (b.runs.length - a.runs.length) || a.name.localeCompare(b.name));

  const controlList = [...controls.values()]
    .map((c) => ({ name: c.name, uses: c.uses, sessions: c.sessions.size }))
    .sort((a, b) => (b.uses - a.uses) || a.name.localeCompare(b.name));

  const actorList = [...actors.values()]
    .map((a) => ({ id: a.id, name: a.name, runs: a.runs, skills: a.skills.size, sessions: a.sessions.size }))
    .sort((a, b) => (b.runs - a.runs) || a.name.localeCompare(b.name));

  const totalRuns = toolRuns + commandRuns;
  return {
    skills: skillList,
    controls: controlList,
    actors: actorList,
    totals: {
      runs: totalRuns,
      skills: skillList.length,
      controlUses: controlList.reduce((s, c) => s + c.uses, 0),
      sessions: new Set((runs || []).filter((r) => !r.builtin).map((r) => r.sessionId)).size,
      attributedMarks: attributed,
      unattributedMarks: unattributed,
      sources: { tool: toolRuns, command: commandRuns },
    },
  };
}
